import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, ArrowUpDown, Trash2 } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { fmtEUR } from "../lib/format";
import { InfoTip } from "../components/InfoTip";

const STORE_BADGE = [
  "bg-indigo-50 text-indigo-700 border-indigo-200",
  "bg-emerald-50 text-emerald-700 border-emerald-200",
  "bg-amber-50 text-amber-700 border-amber-200",
  "bg-rose-50 text-rose-700 border-rose-200",
  "bg-sky-50 text-sky-700 border-sky-200",
  "bg-violet-50 text-violet-700 border-violet-200",
];

export default function ProdottiPage() {
  const { stores, storeId, isMaster } = useOutletContext();
  const [data, setData] = useState(null);
  const [storeSel, setStoreSel] = useState(storeId || "all");
  const [q, setQ] = useState("");
  const [qDeb, setQDeb] = useState("");
  const [filtro, setFiltro] = useState("");
  const [soglia, setSoglia] = useState("");
  const [sort, setSort] = useState({ campo: "nome", dir: "asc" });
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(100);
  const [nuovo, setNuovo] = useState({ sku: "", nome: "", costo: "" });
  const effStore = storeSel !== "all" ? storeSel : stores[0]?.id;

  useEffect(() => { setStoreSel(storeId || "all"); }, [storeId]);
  useEffect(() => {
    const t = setTimeout(() => setQDeb(q), 350);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => { setPage(1); }, [storeSel, qDeb, filtro, soglia, sort, perPage]);

  const load = () => {
    const params = new URLSearchParams({
      store_id: storeSel, sort: sort.campo, dir: sort.dir,
      page: String(page), per_page: String(perPage),
    });
    if (qDeb) params.set("q", qDeb);
    if (filtro) params.set("filtro", filtro);
    if (soglia !== "") params.set("soglia_ricarico", soglia);
    api.get(`/prodotti?${params}`).then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)));
  };
  useEffect(() => { load(); }, [storeSel, qDeb, filtro, soglia, sort, page, perPage]);

  const add = async () => {
    try {
      await api.post("/prodotti", {
        store_id: effStore, sku: nuovo.sku || null, nome: nuovo.nome,
        costo: nuovo.costo === "" ? null : parseFloat(String(nuovo.costo).replace(",", ".")),
      });
      toast.success("Prodotto aggiunto");
      setNuovo({ sku: "", nome: "", costo: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const patch = async (p, campo, valore) => {
    try {
      if (campo === "costo" && valore !== "") valore = parseFloat(String(valore).replace(",", "."));
      await api.patch(`/prodotti/${p.id}`, { [campo]: valore === "" ? null : valore });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleSort = (campo) => setSort((s) => (
    s.campo === campo ? { campo, dir: s.dir === "asc" ? "desc" : "asc" } : { campo, dir: "asc" }
  ));

  const inp = "border border-slate-300 rounded-md px-2 py-1 text-sm";
  const prodotti = data?.prodotti || [];
  const masterStato = data?.master?.stato;
  const storeBadge = (id) => {
    const i = Math.max(0, stores.findIndex((s) => s.id === id));
    return STORE_BADGE[i % STORE_BADGE.length];
  };
  const nCols = isMaster ? 7 : 4;

  const ThSort = ({ campo, children, masterOnly, right, tip }) => (
    (masterOnly && !isMaster) ? null : (
      <th className={`px-3 py-2.5 text-xs font-bold uppercase tracking-wider sticky top-0 bg-slate-100 z-10 ${right ? "text-right" : "text-left"} ${campo === "ricarico" || campo === "costo_reale" ? "text-indigo-600" : "text-slate-600"}`}>
        <button type="button" data-testid={`prodotti-sort-${campo}`} onClick={() => toggleSort(campo)}
          className="inline-flex items-center gap-1 uppercase tracking-wider hover:text-indigo-700 transition-colors">
          {children}
          {sort.campo === campo
            ? (sort.dir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)
            : <ArrowUpDown className="w-3 h-3 opacity-30" />}
        </button>
        {tip}
      </th>
    )
  );

  return (
    <div data-testid="prodotti-page" className="space-y-5">
      <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Prodotti</h1>
      {!isMaster && (
        <p className="text-xs text-slate-500 bg-indigo-50 border border-indigo-200 rounded-md px-3 py-2 max-w-2xl">
          Il costo che vedi qui è il prezzo di cessione del tuo store. Il costo reale del fornitore non è visibile ai soci.
        </p>
      )}
      {isMaster && masterStato === "senza_prodotti" && (
        <p data-testid="prodotti-master-avviso" className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 max-w-2xl">
          Lo store master ({data.master.nome}) non ha ancora prodotti importati: "Costo reale" e "Ricarico" non sono calcolabili finché il suo catalogo non viene sincronizzato.
        </p>
      )}
      {isMaster && masterStato === "non_configurato" && (
        <p data-testid="prodotti-master-avviso" className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 max-w-2xl">
          Nessuno store master configurato: "Costo reale" e "Ricarico" non sono calcolabili.
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2 bg-white border border-slate-200 rounded-lg p-3" data-testid="prodotti-filtri">
        <input data-testid="prodotti-search" className={inp + " w-64"} placeholder="Cerca per SKU o nome…"
          value={q} onChange={(e) => setQ(e.target.value)} />
        <select data-testid="prodotti-store-filter" className={inp} value={storeSel} onChange={(e) => setStoreSel(e.target.value)}>
          <option value="all">Tutti gli store</option>
          {stores.map((s) => <option key={s.id} value={s.id}>{s.nome}</option>)}
        </select>
        {isMaster && (
          <select data-testid="prodotti-filtro-rapido" className={inp} value={filtro} onChange={(e) => setFiltro(e.target.value)}>
            <option value="">Tutti i prodotti</option>
            <option value="ricostruiti">Solo costo ricostruito (≈)</option>
            <option value="dati_mancanti">Solo dati mancanti</option>
          </select>
        )}
        {isMaster && (
          <label className="text-xs text-slate-500 flex items-center gap-1.5">
            Ricarico sotto €
            <input data-testid="prodotti-soglia-ricarico" type="number" step="0.5" className={inp + " w-24"} placeholder="es. 2"
              value={soglia} onChange={(e) => setSoglia(e.target.value)} />
          </label>
        )}
        {data && (
          <span className="text-xs text-slate-400 ml-auto" data-testid="prodotti-totale">
            {data.totale.toLocaleString("it-IT")} prodotti
          </span>
        )}
      </div>

      <div className="flex gap-2 items-end bg-white border border-slate-200 rounded-lg p-4 max-w-3xl">
        <label className="text-xs text-slate-500">SKU<input data-testid="prodotto-sku" className={inp + " w-32 block mt-1"} value={nuovo.sku} onChange={(e) => setNuovo({ ...nuovo, sku: e.target.value })} /></label>
        <label className="text-xs text-slate-500 flex-1">Nome<input data-testid="prodotto-nome" className={inp + " block mt-1 w-full"} value={nuovo.nome} onChange={(e) => setNuovo({ ...nuovo, nome: e.target.value })} /></label>
        <label className="text-xs text-slate-500">Costo cessione €<input data-testid="prodotto-costo" className={inp + " w-28 block mt-1"} value={nuovo.costo} onChange={(e) => setNuovo({ ...nuovo, costo: e.target.value })} /></label>
        <button data-testid="prodotto-add-button" onClick={add} disabled={!nuovo.nome || !effStore}
          className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-md px-4 py-1.5 disabled:opacity-40">Aggiungi</button>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg">
        <div className="overflow-auto max-h-[calc(100vh-19rem)]">
          <table data-testid="prodotti-table" className="w-full text-sm">
            <thead>
              <tr className="text-left">
                <ThSort campo="sku">SKU</ThSort>
                <ThSort campo="nome">Nome</ThSort>
                {isMaster && <th className="px-3 py-2.5 text-xs font-bold uppercase tracking-wider text-slate-600 sticky top-0 bg-slate-100 z-10">Store</th>}
                <ThSort campo="costo" right>
                  Costo cessione
                  <InfoTip text="Prezzo di cessione praticato allo store: è il costo merce che entra nei margini dello store e nelle quote socio" testid="tip-cessione" />
                </ThSort>
                <ThSort campo="costo_reale" masterOnly right>
                  Costo reale
                  <InfoTip text="Costo al fornitore letto dal catalogo dello store master con lo stesso SKU. Mai visibile ai soci" testid="tip-costo-reale" />
                </ThSort>
                <ThSort campo="ricarico" masterOnly right>
                  Ricarico
                  <InfoTip text="Cessione meno costo reale, per pezzo: quanto resta al gruppo sulla merce. Clicca per ordinare: i ricarichi troppo bassi o negativi sono errori di prezzo da correggere" testid="tip-ricarico" />
                </ThSort>
                <th className="px-3 py-2.5 sticky top-0 bg-slate-100 z-10" />
              </tr>
            </thead>
            <tbody>
              {prodotti.map((p) => (
                <tr key={p.id} data-testid={`prodotto-row-${p.sku || p.id}`} className="border-t border-slate-100">
                  <td className="px-3 py-1.5">
                    <input className={inp + " w-52 font-mono text-xs"} defaultValue={p.sku || ""} placeholder="mancante"
                      title={p.sku || "SKU mancante"}
                      onBlur={(e) => e.target.value !== (p.sku || "") && patch(p, "sku", e.target.value)} />
                  </td>
                  <td className="px-3 py-1.5">
                    <input className={inp + " w-full min-w-[200px]"} defaultValue={p.nome} title={p.nome}
                      onBlur={(e) => e.target.value !== p.nome && patch(p, "nome", e.target.value)} />
                  </td>
                  {isMaster && (
                    <td className="px-3 py-1.5">
                      <span className={`text-[10px] font-semibold border rounded-full px-2 py-0.5 whitespace-nowrap ${storeBadge(p.store_id)}`}>
                        {stores.find((s) => s.id === p.store_id)?.nome}
                      </span>
                    </td>
                  )}
                  <td className="px-3 py-1.5 text-right">
                    <input data-testid="prodotto-costo-cell" className={inp + " w-28 text-right font-mono"} defaultValue={p.costo ?? ""} placeholder="mancante"
                      onBlur={(e) => e.target.value !== String(p.costo ?? "") && patch(p, "costo", e.target.value)} />
                    {p.costo != null && <span className="ml-2 text-xs text-slate-400 font-mono">{fmtEUR(p.costo)}</span>}
                  </td>
                  {isMaster && (
                    <td data-testid={`prodotto-costo-reale-${p.sku || p.id}`} className="px-3 py-1.5 text-right font-mono text-xs whitespace-nowrap">
                      {p.costo_reale != null ? (
                        <>
                          {fmtEUR(p.costo_reale)}
                          {p.costo_reale_origine === "ricostruito" && (
                            <span className="ml-1 text-[10px] font-sans font-semibold bg-amber-50 text-amber-700 border border-dashed border-amber-300 rounded px-1 cursor-help"
                              title="Costo ricostruito: prezzo di vendita meno il ricarico fisso della collezione (regola configurabile in Configurazione). Non è un costo letto dal fornitore">≈ ricostr.</span>
                          )}
                        </>
                      ) : p.sul_master === false ? <span className="text-amber-600">SKU assente sul master</span>
                        : p.sul_master ? <span className="text-amber-600">senza costo sul master</span>
                        : <span className="text-slate-400">—</span>}
                    </td>
                  )}
                  {isMaster && (
                    <td data-testid={`prodotto-ricarico-${p.sku || p.id}`} className="px-3 py-1.5 text-right font-mono text-xs">
                      {p.ricarico != null ? (
                        <span className={`${p.ricarico_stimato ? "cursor-help" : ""} ${p.ricarico < 0 ? "text-red-600 font-bold" : p.ricarico < 1 ? "text-amber-600 font-semibold" : ""}`}
                          title={p.ricarico_stimato ? "Ricarico stimato: deriva da un costo ricostruito, non misurato" : undefined}>
                          {p.ricarico_stimato ? "≈ " : ""}{fmtEUR(p.ricarico)}
                        </span>
                      ) : "—"}
                    </td>
                  )}
                  <td className="px-3 py-1.5 text-right">
                    <button onClick={async () => { await api.delete(`/prodotti/${p.id}`); load(); }} className="text-slate-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
              {data && prodotti.length === 0 && (
                <tr><td colSpan={nCols} className="px-4 py-8 text-center text-slate-400">Nessun prodotto con questi filtri.</td></tr>
              )}
              {!data && (
                <tr><td colSpan={nCols} className="px-4 py-8 text-center text-slate-400">Caricamento prodotti…</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {data && data.totale > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 border-t border-slate-200">
            <span className="text-xs text-slate-500" data-testid="prodotti-page-info">
              Pagina {data.pagina} di {data.pagine} · {data.totale.toLocaleString("it-IT")} prodotti
            </span>
            <div className="flex items-center gap-2">
              <select data-testid="prodotti-per-page" className={inp} value={perPage}
                onChange={(e) => setPerPage(parseInt(e.target.value))}>
                {[50, 100, 200, 500].map((n) => <option key={n} value={n}>{n} per pagina</option>)}
              </select>
              <button data-testid="prodotti-prev" onClick={() => setPage((v) => Math.max(1, v - 1))} disabled={page <= 1}
                className="text-xs font-semibold border border-slate-300 rounded px-3 py-1.5 hover:bg-slate-50 disabled:opacity-40">← Prec.</button>
              <button data-testid="prodotti-next" onClick={() => setPage((v) => v + 1)} disabled={page >= (data?.pagine || 1)}
                className="text-xs font-semibold border border-slate-300 rounded px-3 py-1.5 hover:bg-slate-50 disabled:opacity-40">Succ. →</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
